import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Popover,
  TextField,
  Checkbox,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { Search } from "lucide-react";
import { ACCENT_COLOR as accentColor } from "../../utils/constants";
import useIsDarkTheme from "../../hooks/useIsDarkTheme";

const MultiSelect = ({
  options = [],
  selectedValues = {},
  onSelectionChange,
  buttonLabel = "Select options",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found",
  buttonIcon,
  getOptionLabel = option => option.label || option.name || String(option),
  getOptionId = option => option.id || option.key || String(option),
  searchFilter = (option, searchText) => {
    const label = getOptionLabel(option).toLowerCase();
    const id = getOptionId(option).toLowerCase();
    const searchLower = searchText.toLowerCase();
    return label.includes(searchLower) || id.includes(searchLower);
  },
  buttonProps = {},
  popoverProps = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const isDark = useIsDarkTheme();

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const open = Boolean(anchorEl);

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    return options.filter(option => searchFilter(option, searchText));
  }, [options, searchText, searchFilter]);

  // Calculate select all state
  const selectAllState = useMemo(() => {
    if (filteredOptions.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const filteredSelectedCount = filteredOptions.filter(option => {
      const id = getOptionId(option);
      return selectedValues[id] !== false;
    }).length;

    const checked = filteredSelectedCount === filteredOptions.length;
    const indeterminate = filteredSelectedCount > 0 && filteredSelectedCount < filteredOptions.length;

    return { checked, indeterminate };
  }, [filteredOptions, selectedValues, getOptionId]);

  const handleToggle = id => {
    onSelectionChange?.(id);
  };

  const handleSelectAll = () => {
    const allSelected = selectAllState.checked;
    const shouldSelectAll = !allSelected; // If all are selected, deselect all; otherwise select all

    // Toggle all filtered options to the desired state
    filteredOptions.forEach(option => {
      const id = getOptionId(option);
      const isCurrentlySelected = selectedValues[id] !== false;
      // Only toggle if the current state doesn't match the desired state
      if (isCurrentlySelected !== shouldSelectAll) {
        onSelectionChange?.(id);
      }
    });
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={handleClick}
        startIcon={buttonIcon}
        sx={{
          textTransform: "none",
          borderColor: open ? accentColor : isDark ? "#374151" : "#d1d5db",
          borderRadius: "10px",
          color: open ? accentColor : isDark ? "#d1d5db" : "#374151",
          fontWeight: 600,
          fontSize: "0.8rem",
          px: 2,
          py: 0.7,
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: accentColor,
            backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
            color: accentColor,
          },
          ...buttonProps.sx,
        }}
        {...buttonProps}
      >
        {buttonLabel}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 260,
            maxWidth: 320,
            maxHeight: 420,
            borderRadius: "12px",
            border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
            background: isDark ? "#1f2937" : "#fff",
            boxShadow: isDark
              ? "0 8px 30px rgba(0, 0, 0, 0.3)"
              : "0 8px 30px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
            ...popoverProps.PaperProps?.sx,
          },
          ...popoverProps.PaperProps,
        }}
        {...popoverProps}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={15} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "0.8rem",
                backgroundColor: isDark ? "#283040" : "#fafbfc",
                color: isDark ? "#f9fafb" : undefined,
                "& fieldset": { borderColor: isDark ? "#374151" : "#e5e7eb" },
                "&:hover fieldset": { borderColor: isDark ? "#4b5563" : "#d1d5db" },
                "&.Mui-focused fieldset": {
                  borderColor: accentColor,
                  borderWidth: "1.5px",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: isDark ? "#6b7280" : "#9ca3af",
                opacity: 1,
              },
              "& .MuiInputBase-input": {
                color: isDark ? "#f9fafb" : undefined,
              },
            }}
          />
          <List
            dense
            sx={{
              maxHeight: 300,
              overflow: "auto",
              p: 0,
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "#4b5563" : "#d1d5db",
                borderRadius: 4,
              },
            }}
          >
            {filteredOptions.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={emptyMessage}
                  sx={{ textAlign: "center" }}
                  primaryTypographyProps={{ color: "#9ca3af", fontSize: "0.8rem" }}
                />
              </ListItem>
            ) : (
              <>
                {/* Select All option */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleSelectAll}
                    dense
                    sx={{
                      py: 0.6,
                      px: 1,
                      borderBottom: isDark ? "1px solid #374151" : "1px solid #f0f0f0",
                      borderRadius: "6px 6px 0 0",
                      "&:hover": {
                        backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox
                        checked={selectAllState.checked}
                        indeterminate={selectAllState.indeterminate}
                        size="small"
                        edge="start"
                        sx={{
                          color: isDark ? "#4b5563" : "#d1d5db",
                          "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: accentColor },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Select All"
                      primaryTypographyProps={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: isDark ? "#e5e7eb" : "#1f2937",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {filteredOptions.map(option => {
                  const id = getOptionId(option);
                  const label = getOptionLabel(option);
                  const isSelected = selectedValues[id] !== false;

                  return (
                    <ListItem key={id} disablePadding>
                      <ListItemButton
                        onClick={() => handleToggle(id)}
                        dense
                        sx={{
                          py: 0.5,
                          px: 1,
                          borderRadius: "4px",
                          transition: "background-color 0.12s ease",
                          "&:hover": {
                            backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            edge="start"
                            sx={{
                              color: isDark ? "#4b5563" : "#d1d5db",
                              "&.Mui-checked": { color: accentColor },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{
                            fontSize: "0.8rem",
                            color: isSelected ? (isDark ? "#e5e7eb" : "#1f2937") : isDark ? "#9ca3af" : "#6b7280",
                            fontWeight: isSelected ? 500 : 400,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.array.isRequired,
  selectedValues: PropTypes.object.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  buttonIcon: PropTypes.node,
  getOptionLabel: PropTypes.func,
  getOptionId: PropTypes.func,
  searchFilter: PropTypes.func,
  buttonProps: PropTypes.object,
  popoverProps: PropTypes.object,
};

export default MultiSelect;
